import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi } from '@tanstack/react-router'
import { DownloadIcon, SearchIcon, XIcon } from 'lucide-react'
import { useMemo } from 'react'
import { CariLink } from '@app/components/entity-link'
import { ScreenHeader } from '@app/components/screen-header'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'
import { finansApi, finansQueryKeys } from '@app/features/finans/api'
import type {
  ChequeCurrencyTotal,
  ChequeDirection,
  ChequeRow,
} from '@app/features/finans/api'
import { formatAmount, formatDate, formatDateTime } from '@app/features/finans/format'
import { FreshnessStrip } from '@app/features/finans/freshness-strip'
import { FinansTabs } from '@app/features/finans/tabs'
import { MaturityLadder } from '@app/features/finans/ladder'
import { ChequeStatusBadge, EmptyState, ErrorState, TableSkeleton } from '@app/features/finans/ui'

/**
 * Çek Takvimi — the cheque book as a maturity calendar.
 *
 * The advisor's weekly Excel hand-copies exactly this block from Korgun screens,
 * and the reconciliation found the copy missing 7.15M TL of cards. This screen
 * is the zero-entry replacement: the ladder answers "what falls due when", the
 * table is the register, the CSV feeds the advisor's file.
 *
 * Wording discipline (from the design review): "vadesi geçmiş" is a date fact,
 * never a payment claim — Korgun does not close own cheques. The received view
 * is titled "Alınan çekler" (lifecycle-neutral): it includes paper endorsed
 * onward, which is no longer in the portfolio.
 */

const route = getRouteApi('/_korumali/finans')

const DIRECTION_CHIPS: { value: ChequeDirection; label: string }[] = [
  { value: 'issued', label: 'Ödenecek çekler' },
  { value: 'received', label: 'Alınan çekler' },
]

const COLUMN = {
  debtor: 'hidden xl:table-cell',
  bank: 'hidden lg:table-cell',
  entered: 'hidden 2xl:table-cell',
} as const

function StatBlock({
  label,
  totals,
  tone = 'neutral',
  sub,
}: {
  label: string
  totals: readonly ChequeCurrencyTotal[]
  tone?: 'neutral' | 'warn'
  /** Visible qualifier line — wording constraints live here, not in tooltips. */
  sub?: string
}) {
  return (
    <div className="min-w-36">
      <p className={cn('eyebrow', tone === 'warn' && 'text-hardal')}>{label}</p>
      {sub ? <p className="text-micro text-ink-muted">{sub}</p> : null}
      {totals.length === 0 ? (
        <p className="mt-0.5 font-mono text-data tnum text-ink-muted/60">—</p>
      ) : (
        totals.map((total) => (
          <p key={total.currency} className="mt-0.5 leading-tight">
            <span
              className={cn(
                'font-mono text-data font-semibold tnum',
                tone === 'warn' ? 'text-hardal' : 'text-ink',
              )}
            >
              {formatAmount(total.amount, total.currency)}
            </span>{' '}
            <span className="text-micro text-ink-muted">{total.count} adet</span>
          </p>
        ))
      )}
    </div>
  )
}

export function FinansScreen() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const csv = useCsvDownload()

  const direction: ChequeDirection = search.direction ?? 'issued'
  const query = search.query ?? ''

  const [searchInput, setSearchInput, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({ ...prev, query: text === '' ? undefined : text }),
      replace: true,
      resetScroll: false,
    })
  })

  const params = useMemo(() => ({ direction, query }), [direction, query])

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: finansQueryKeys.cekler(params),
    queryFn: () => finansApi.cekler(params),
    placeholderData: keepPreviousData,
    // The maturity classes roll over at Istanbul midnight; a screen left open
    // must not keep yesterday's calendar. Modest interval, not a midnight timer.
    refetchInterval: 15 * 60_000,
  })

  /*
   * `keepPreviousData` keeps the table steady while a SEARCH settles — but a
   * DIRECTION switch swaps headings and layout immediately, and issued totals
   * must never render under "Alınan çekler". Data echoing the other direction
   * is treated as absent (skeleton), same-direction stale data only dims.
   */
  const view = data !== undefined && data.filters.direction === direction ? data : undefined
  const loading = !isError && (isPending || view === undefined)

  const rows = view?.rows ?? []
  const totals = view?.totals
  const isReceived = direction === 'received'
  const columnCount = isReceived ? 8 : 7
  const dimmed = isFetching && !loading

  return (
    <div className="flex flex-col gap-4">
      <FinansTabs />

      <ScreenHeader
        title="Çek Takvimi"
        description={
          isReceived
            ? 'Alınan çek ve senetler — portföydekiler ve ciro edilenler, Korgun çek kartlarından.'
            : 'Ödenecek çek ve senetler — vade takvimi doğrudan Korgun çek kartlarından, elle giriş yok.'
        }
        actions={
          <>
            {csv.error ? (
              <span role="alert" className="text-micro text-hardal">
                {csv.error}
              </span>
            ) : null}
            <Button
              variant="outline"
              disabled={csv.busy}
              aria-busy={csv.busy}
              onClick={() =>
                csv.start('/finans/cekler/export', {
                  direction: direction === 'issued' ? undefined : direction,
                  // flush() hands the export text still waiting on the debounce.
                  query: flushSearch() || undefined,
                })
              }
            >
              <DownloadIcon aria-hidden />
              {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
            </Button>
          </>
        }
      />

      <FreshnessStrip />

      <div className="flex flex-wrap items-center gap-2">
        {DIRECTION_CHIPS.map((chip) => (
          <Button
            key={chip.value}
            size="sm"
            variant={direction === chip.value ? 'primary' : 'outline'}
            aria-pressed={direction === chip.value}
            onClick={() => {
              // A filter write carries text still waiting on the debounce and
              // must not jump the viewport (the repo-wide flush contract).
              const text = flushSearch({ commit: false })
              void navigate({
                search: (prev) => ({
                  ...prev,
                  direction: chip.value === 'issued' ? undefined : chip.value,
                  query: text === '' ? undefined : text,
                }),
                resetScroll: false,
              })
            }}
          >
            {chip.label}
          </Button>
        ))}
      </div>

      {totals ? (
        <Card className={cn(dimmed && 'opacity-60 transition-opacity')}>
          <CardContent className="flex flex-wrap gap-x-8 gap-y-3">
            <StatBlock label="Vadesi gelecek" totals={totals.forward} />
            <StatBlock
              label="Vadesi geçmiş"
              tone="warn"
              totals={totals.overdue}
              sub="Korgun'da kapanış kaydı yok"
            />
            {isReceived ? <StatBlock label="Ciro edildi" totals={totals.endorsed} /> : null}
            {totals.undated.length > 0 ? (
              <StatBlock label="Vade bilinmiyor" tone="warn" totals={totals.undated} />
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {view && (view.ladder.length > 0 || (totals?.overdue.length ?? 0) > 0) ? (
        <Card className={cn(dimmed && 'opacity-60 transition-opacity')}>
          <CardHeader>
            <CardTitle>Vade takvimi</CardTitle>
          </CardHeader>
          <CardContent>
            <MaturityLadder
              ladder={view.ladder}
              overdue={totals?.overdue ?? []}
              asOfDate={view.asOfDate}
            />
          </CardContent>
        </Card>
      ) : null}

      {view && view.counterparties.length > 0 ? (
        <Card className={cn(dimmed && 'opacity-60 transition-opacity')}>
          <CardHeader>
            <CardTitle>{isReceived ? 'Keşideciler' : 'En yüklü karşı taraflar'}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-x-8 gap-y-1.5 sm:grid-cols-2">
            {view.counterparties.map((entry) => {
              const currencyMax = Math.max(
                ...view.counterparties
                  .filter((other) => other.currency === entry.currency)
                  .map((other) => other.amount),
                1,
              )
              return (
                <div
                  key={`${entry.currency}|${entry.kod ?? ''}`}
                  className="flex items-center gap-3"
                >
                  <span className="min-w-0 flex-1 truncate text-data text-ink">
                    {entry.kod ? (
                      <CariLink ckod={entry.kod}>{entry.name ?? entry.kod}</CariLink>
                    ) : (
                      (entry.name ?? '—')
                    )}
                  </span>
                  <span aria-hidden className="h-1 w-16 shrink-0 rounded-xs bg-hairline">
                    <span
                      className="block h-full rounded-xs bg-celik"
                      style={{ width: `${Math.max(6, Math.round((entry.amount / currencyMax) * 100))}%` }}
                    />
                  </span>
                  <span className="shrink-0 font-mono text-data tnum text-ink">
                    {formatAmount(entry.amount, entry.currency)}
                  </span>
                  <span className="w-12 shrink-0 text-right text-micro text-ink-muted">
                    {entry.count} adet
                  </span>
                </div>
              )
            })}
          </CardContent>
        </Card>
      ) : null}

      <div className="relative max-w-xs">
        <SearchIcon
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-muted"
        />
        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="Çek no, karşı taraf ara"
          aria-label="Çek ara"
          className="pl-8"
        />
        {searchInput !== '' ? (
          <button
            type="button"
            aria-label="Aramayı temizle"
            className="absolute top-1/2 right-2 -translate-y-1/2 text-ink-muted hover:text-ink"
            onClick={() => setSearchInput('')}
          >
            <XIcon className="size-3.5" aria-hidden />
          </button>
        ) : null}
      </div>

      <TableScroller className={cn(dimmed && 'opacity-60 transition-opacity')}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Vade</TableHead>
              <TableHead>Çek no</TableHead>
              <TableHead>Karşı taraf</TableHead>
              {isReceived ? <TableHead className={COLUMN.debtor}>Borçlu</TableHead> : null}
              <TableHead className={COLUMN.bank}>Banka</TableHead>
              <TableHead className="text-right">Tutar</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead className={COLUMN.entered}>Giriş</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableSkeleton rows={10} cols={columnCount} />
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <ErrorState
                    message={error instanceof Error ? error.message : 'Bilinmeyen hata.'}
                    onRetry={() => void refetch()}
                  />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <EmptyState
                    title="Çek bulunamadı"
                    description={
                      query !== ''
                        ? 'Arama hiçbir çekle eşleşmedi. Filtreyi temizleyip yeniden deneyin.'
                        : 'Bu görünümde kayıtlı çek yok.'
                    }
                    action={
                      query !== '' ? (
                        <Button variant="outline" size="sm" onClick={() => setSearchInput('')}>
                          Aramayı temizle
                        </Button>
                      ) : undefined
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row: ChequeRow) => (
                <TableRow key={row.cekInx}>
                  <TableCell
                    className={cn(
                      'font-mono tnum whitespace-nowrap',
                      !row.endorsed && row.maturity === 'overdue'
                        ? 'font-medium text-hardal'
                        : 'text-ink',
                    )}
                  >
                    {formatDate(row.vade)}
                  </TableCell>
                  <TableCell className="font-mono tnum">
                    <span className="inline-flex items-center gap-1.5">
                      {row.cekNo === '' ? '—' : row.cekNo}
                      {row.kind === 'note' ? <Badge tone="info">Senet</Badge> : null}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-0">
                    <span className="block truncate text-ink">
                      {row.counterpartyKod ? (
                        <CariLink ckod={row.counterpartyKod}>
                          {row.counterpartyName ?? row.counterpartyKod}
                        </CariLink>
                      ) : (
                        (row.counterpartyName ?? '—')
                      )}
                    </span>
                    <span className="block truncate font-mono text-micro tnum text-ink-muted">
                      {row.counterpartyKod ?? ''}
                    </span>
                  </TableCell>
                  {isReceived ? (
                    <TableCell className={cn(COLUMN.debtor, 'max-w-0')}>
                      <span className="block truncate text-ink-muted">{row.debtorName ?? '—'}</span>
                    </TableCell>
                  ) : null}
                  <TableCell className={cn(COLUMN.bank, 'max-w-0')}>
                    <span className="block truncate text-ink-muted">{row.bank ?? '—'}</span>
                  </TableCell>
                  <TableNumCell className="whitespace-nowrap font-medium">
                    {formatAmount(row.amount, row.currency)}
                  </TableNumCell>
                  <TableCell>
                    <ChequeStatusBadge row={row} />
                  </TableCell>
                  <TableCell className={cn(COLUMN.entered, 'whitespace-nowrap')}>
                    <span className="font-mono text-micro tnum text-ink-muted">
                      {formatDateTime(row.enteredAt)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableScroller>

      <p className="mt-2 text-micro text-ink-muted">
        Kaynak: Korgun çek kartları. Korgun ödenen çeki kapatmaz — &quot;Vadesi geçmiş&quot; yalnızca
        vade tarihinin geçtiğini gösterir, ödeme bilgisi değildir. Tutarlar para birimi bazında ayrı
        izlenir, hiçbir zaman toplanmaz.
        {isReceived
          ? ' Not: 06.08.2026 mutabakatında en yeni alınan çek kaydı 08.05.2026 tarihliydi — o tarihten beri çek alınmadığı mı yoksa girilmediği mi veriden ayırt edilemez.'
          : ''}
      </p>
    </div>
  )
}
